import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import TemplateDesigner, { newDesign } from "./TemplateDesigner";
import { Palette, Plus, PenTool, Eye, Trash2, X, RefreshCw, Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

const LAYOUT_LABELS = {
  "sidebar-left": "rbTplLayoutLeft",
  "sidebar-right": "rbTplLayoutRight",
  "single-column": "rbTplLayoutSingle",
};
const FONT_LABELS = { helvetica: "rbTplFontModern", times: "rbTplFontClassic", courier: "rbTplFontMono" };

export default function TemplateManager() {
  const confirm = useConfirm();
  const { t } = useT();
  const [templates, setTemplates] = useState(null);
  const [editing, setEditing] = useState(null); // { id?, name, design }
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [liveUrl, setLiveUrl] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/cv-templates")
      .then(({ data }) => setTemplates(data))
      .catch(() => setTemplates([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Realtime preview: re-render the PDF ~1s after the last design change
  useEffect(() => {
    if (!editing) {
      setLiveUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
      return;
    }
    const timer = setTimeout(async () => {
      setLiveBusy(true);
      try {
        const res = await api.post("/cv-templates/preview", { design: editing.design }, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setLiveUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
      } catch {
        // keep the previous preview on transient errors
      } finally {
        setLiveBusy(false);
      }
    }, editing.id || liveUrl ? 1000 : 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.design, editing ? "y" : "n"]);

  const openPreview = async (design) => {
    setPreviewBusy(true);
    try {
      const res = await api.post("/cv-templates/preview", { design }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
    } catch {
      toast.error(t("rbPdfFailed"));
    } finally {
      setPreviewBusy(false);
    }
  };

  const closePreview = () => {
    setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
  };

  const save = async () => {
    const name = editing.name.trim();
    if (!name) { toast.error(t("rbTplNameRequired")); return; }
    setBusy(true);
    try {
      if (editing.id) {
        await api.put(`/cv-templates/${editing.id}`, { name, design: editing.design });
      } else {
        await api.post("/cv-templates", { name, design: editing.design });
      }
      toast.success(t("tmSaved"));
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("rbSaveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (tpl) => {
    if (!(await confirm(t("tmDeleteConfirm")))) return;
    try {
      await api.delete(`/cv-templates/${tpl.id}`);
      toast.success(t("tmDeleted"));
      load();
    } catch {
      toast.error(t("dashDeleteFailed"));
    }
  };

  return (
    <section data-testid="templates-view">
      <p className="ed-eyebrow flex items-center gap-2 mb-2">
        <Palette size={12} strokeWidth={1.5} /> {t("navTemplates")}
      </p>

      {editing ? (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-serif text-2xl italic text-neutral-800" data-testid="template-editor-title">
              {editing.id ? t("tmEditTitle") : t("tmNewTitle")}
            </p>
            <button data-testid="template-editor-back" className="ed-btn ed-btn-outline" onClick={() => setEditing(null)}>
              <ArrowLeft size={14} strokeWidth={1.5} /> {t("tmCancel")}
            </button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_30rem] gap-8 items-start">
            <div className="space-y-6 min-w-0">
              <div className="ed-card p-4 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[14rem]">
                  <label className="ed-label">{t("tmNameLabel")}</label>
                  <input
                    data-testid="template-name-input"
                    className="ed-input font-serif text-lg"
                    placeholder={t("rbTplNamePh")}
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                </div>
                <button data-testid="template-save-btn" className="ed-btn" onClick={save} disabled={busy}>
                  <Check size={14} strokeWidth={1.5} /> {busy ? t("rbAutosaving") : t("tmSave")}
                </button>
              </div>

              <TemplateDesigner
                design={editing.design}
                onChange={(design) => setEditing({ ...editing, design })}
              />
            </div>

            {/* Realtime preview */}
            <div className="xl:sticky xl:top-4" data-testid="template-live-preview">
              <div className="ed-card p-0 overflow-hidden flex flex-col h-[75vh]">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-neutral-200 bg-white">
                  <p className="ed-eyebrow flex items-center gap-2">
                    <Eye size={11} strokeWidth={1.5} /> {t("tmLivePreview")}
                  </p>
                  <span className={`font-mono text-[0.6rem] uppercase tracking-[0.12em] inline-flex items-center gap-1.5 ${liveBusy ? "text-neutral-500" : "text-emerald-700"}`}>
                    {liveBusy ? (
                      <><RefreshCw size={10} strokeWidth={1.5} className="animate-spin" /> {t("rbPreviewBusy")}</>
                    ) : (
                      <><Check size={10} strokeWidth={2} /> {t("rbAutosaved")}</>
                    )}
                  </span>
                </div>
                {liveUrl ? (
                  <iframe title="template-live-preview" src={`${liveUrl}#toolbar=0&navpanes=0&scrollbar=0`} className="flex-1 w-full bg-neutral-200" />
                ) : (
                  <div className="flex-1 flex items-center justify-center bg-neutral-100">
                    <p className="ed-eyebrow">{t("loading")}</p>
                  </div>
                )}
              </div>
              <p className="text-[0.65rem] font-mono text-neutral-500 mt-2">{t("tmPreviewHint")}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-serif text-2xl italic text-neutral-800">{t("tmSubtitle")}</p>
            <button
              data-testid="template-new-btn"
              className="ed-btn"
              onClick={() => setEditing({ name: "", design: newDesign() })}
            >
              <Plus size={14} strokeWidth={1.5} /> {t("tmNewBtn")}
            </button>
          </div>
          <p className="text-xs font-mono text-neutral-500 -mt-3">{t("tmHint")}</p>

          {templates === null ? (
            <p className="ed-eyebrow">{t("loading")}</p>
          ) : templates.length === 0 ? (
            <div className="ed-card p-10 text-center" data-testid="templates-empty">
              <Palette size={22} strokeWidth={1.5} className="mx-auto text-neutral-400" />
              <p className="font-serif text-xl mt-3">{t("tmEmptyTitle")}</p>
              <p className="text-sm text-neutral-500 mt-1">{t("tmEmptyHint")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="templates-list">
              {templates.map((tpl, i) => (
                <div key={tpl.id} className="ed-card p-5" data-testid={`template-card-${i}`}>
                  <div className="flex items-start gap-3">
                    <span className="w-8 h-8 shrink-0 border border-neutral-300" style={{ background: tpl.design?.accent || "#1A1A1A" }} />
                    <div className="min-w-0 flex-1">
                      <p className="font-serif text-xl truncate" data-testid={`template-card-name-${i}`}>{tpl.name}</p>
                      <p className="font-mono text-[0.65rem] uppercase tracking-[0.12em] text-neutral-500 mt-0.5">
                        {t(LAYOUT_LABELS[tpl.design?.layout] || "rbTplLayoutLeft")} · {t(FONT_LABELS[tpl.design?.font] || "rbTplFontModern")}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-neutral-200">
                    <button
                      data-testid={`template-edit-${i}`}
                      className="ed-btn ed-btn-outline"
                      onClick={() => setEditing({ id: tpl.id, name: tpl.name, design: { ...newDesign(), ...tpl.design } })}
                    >
                      <PenTool size={13} strokeWidth={1.5} /> {t("tmEdit")}
                    </button>
                    <button
                      data-testid={`template-preview-${i}`}
                      className="ed-btn ed-btn-outline"
                      onClick={() => openPreview(tpl.design)}
                      disabled={previewBusy}
                    >
                      <Eye size={13} strokeWidth={1.5} /> {t("rbPreview")}
                    </button>
                    <button
                      data-testid={`template-delete-${i}`}
                      onClick={() => remove(tpl)}
                      className="p-2 border border-neutral-300 hover:border-rose-700 hover:bg-rose-700 hover:text-white transition-colors ml-auto"
                      title={t("delete")}
                    >
                      <Trash2 size={13} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* PDF preview modal */}
      {previewUrl && (
        <div className="fixed inset-0 lg:left-72 z-50" data-testid="template-preview-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={closePreview} />
          <div className="absolute inset-4 sm:inset-10 bg-[#F9F8F6] border border-neutral-300 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
              <p className="ed-eyebrow">{t("rbPreviewTitle")}</p>
              <div className="flex items-center gap-2">
                <p className="text-[0.65rem] font-mono text-neutral-500 hidden sm:block">{t("tmPreviewHint")}</p>
                {editing && (
                  <button data-testid="template-preview-refresh" className="ed-btn ed-btn-outline" onClick={() => openPreview(editing.design)} disabled={previewBusy}>
                    <RefreshCw size={13} strokeWidth={1.5} className={previewBusy ? "animate-spin" : ""} /> {t("rbPreviewRefresh")}
                  </button>
                )}
                <button
                  data-testid="template-preview-close"
                  onClick={closePreview}
                  className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
                >
                  <X size={15} strokeWidth={1.5} />
                </button>
              </div>
            </div>
            <iframe title="template-preview" src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`} className="flex-1 w-full bg-neutral-200" />
          </div>
        </div>
      )}
    </section>
  );
}
